import { allLessons } from 'content-collections'
import { type ComponentType, lazy, Suspense } from 'react'
import { data } from 'react-router'
import { mdxComponents } from '#app/components/brand/mdx.tsx'
import {
	findVisible,
	getMdxLoader,
	type MdxLoader,
} from '#app/utils/content.ts'
import { buildMeta, DEFAULT_OG_IMAGE, SITE_URL } from '#app/utils/seo.ts'
import { ArticleLayout } from '../../components/article-layout'
import { MarketingErrorBoundary } from '../../components/marketing-boundary'
import { type Route } from './+types/$slug.ts'

const LOADERS = import.meta.glob('/content/courses/*/index.mdx') as Record<
	string,
	MdxLoader
>

type ArticleComponent = ComponentType<{ components?: typeof mdxComponents }>
const cache = new Map<string, ArticleComponent>()
function lessonFor(slug: string): ArticleComponent | null {
	const load = getMdxLoader(LOADERS, slug)
	if (!load) return null
	let Comp = cache.get(slug)
	if (!Comp) {
		Comp = lazy(load) as ArticleComponent
		cache.set(slug, Comp)
	}
	return Comp
}

export async function loader({ params }: Route.LoaderArgs) {
	const lesson = findVisible(allLessons, params.slug)
	if (!lesson) throw new Response('Not found', { status: 404 })
	return data({
		slug: lesson.slug,
		frontmatter: {
			title: lesson.title,
			description: lesson.description,
			// Map lesson fields onto the shared article layout.
			tag: lesson.module,
			readingTime: lesson.duration ?? lesson.readingTime,
			image: lesson.image,
		},
	})
}

export const meta: Route.MetaFunction = ({ data }) => {
	if (!data) return []
	const path = `/course/lessons/${data.slug}`
	const url = `${SITE_URL}${path}`
	return [
		...buildMeta({
			title: `${data.frontmatter.title} | The Course · Competitor Backlinks`,
			description: data.frontmatter.description,
			path,
			image: data.frontmatter.image,
			type: 'article',
		}),
		{
			'script:ld+json': {
				'@context': 'https://schema.org',
				'@type': 'LearningResource',
				name: data.frontmatter.title,
				description: data.frontmatter.description,
				learningResourceType: 'Lesson',
				...(data.frontmatter.tag
					? { educationalLevel: data.frontmatter.tag }
					: {}),
				image: `${SITE_URL}${data.frontmatter.image ?? DEFAULT_OG_IMAGE}`,
				isPartOf: {
					'@type': 'Course',
					name: 'Run competitor backlink research yourself, with AI',
					url: 'https://competitorbacklinks.com/course',
				},
				url,
			},
		},
	]
}

export default function Lesson({ loaderData }: Route.ComponentProps) {
	const Content = lessonFor(loaderData.slug)
	if (!Content)
		throw new Error(`Missing MDX body for lesson "${loaderData.slug}"`)
	return (
		<ArticleLayout
			frontmatter={loaderData.frontmatter}
			backTo="/course"
			backLabel="Back to the course"
		>
			<Suspense fallback={<div className="h-64" aria-hidden />}>
				<Content components={mdxComponents} />
			</Suspense>
		</ArticleLayout>
	)
}

export function ErrorBoundary() {
	return (
		<MarketingErrorBoundary
			notFoundTitle="We couldn’t find that lesson"
			notFoundBody="It may have moved or isn’t published yet. Head back to the course overview."
		/>
	)
}
